import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaperClipIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlass } from "react-loader-spinner";
import { ToastContainer } from "react-toastify";
import { contextApi } from "../context/contextApi";
import { api, handleToast } from "../services/config";

const STATUS_OPTIONS = [
  { value: "1", label: "باز" },
  { value: "2", label: "در حال بررسی" },
  { value: "3", label: "در انتظار کاربر" },
  { value: "4", label: "حل‌شده" },
  { value: "5", label: "بسته" },
];

const PRIORITY_OPTIONS = [
  { value: "1", label: "کم" },
  { value: "2", label: "معمولی" },
  { value: "3", label: "بالا" },
  { value: "4", label: "فوری" },
];

const CLOSED_STATUS = 5;

const toPersianDigits = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[digit]);
};

const formatMessageDate = (value) => {
  if (!value) return "-";
  const text = String(value);

  // Keep already-localized Persian dates as-is
  if (/^\d{4}-\d{2}-\d{2}/.test(text) && !text.includes("T")) {
    return toPersianDigits(text);
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) {
    return toPersianDigits(text);
  }

  return toPersianDigits(
    date.toLocaleString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  );
};

const getInitial = (name) => {
  const text = String(name || "").trim();
  return text ? text.charAt(0) : "?";
};

const getFieldLabel = (field, fallbackOptions = []) => {
  if (field && typeof field === "object") {
    return field.label || "-";
  }
  if (field === 0 || field) {
    return (
      fallbackOptions.find((item) => item.value === String(field))?.label ||
      String(field)
    );
  }
  return "-";
};

const getFieldValue = (field) => {
  if (field && typeof field === "object") {
    return Number(field.value);
  }
  return Number(field);
};

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  authorization: `Bearer ${localStorage.getItem("login")}`,
});

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.messages)) return payload.messages;
  if (Array.isArray(payload?.data?.messages)) return payload.data.messages;
  return [];
};

const getMessageText = (message) =>
  message?.message ||
  message?.body ||
  message?.content ||
  message?.text ||
  "";

const getAttachments = (message) => {
  const list =
    message?.attachments || message?.media || message?.files || [];
  return Array.isArray(list) ? list : [];
};

const getAttachmentName = (file) =>
  file?.name || file?.file_name || file?.original_name || "پیوست";

const getSiteOrigin = () => {
  const base = api.defaults.baseURL || "";
  try {
    return new URL(base).origin;
  } catch {
    return String(base).replace(/\/api\/v\d+\/?$/, "");
  }
};

const getAttachmentUrl = (file) => {
  // Authenticated download: GET api/v1/media/{uuid}/download
  if (file?.uuid) {
    const apiBase = api.defaults.baseURL || "";
    return `${apiBase}media/${file.uuid}/download`;
  }

  const raw =
    file?.download_url ||
    file?.original_url ||
    file?.full_url ||
    file?.path ||
    (file?.id ? `media/${file.id}/download` : "") ||
    file?.url;

  if (!raw) return "";
  const path = String(raw);

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  if (path.includes("media/")) {
    const apiBase = api.defaults.baseURL || "";
    return `${apiBase}${path.replace(/^\//, "")}`;
  }

  if (path.startsWith("/")) {
    return `${getSiteOrigin()}${path}`;
  }

  const apiBase = api.defaults.baseURL || "";
  return `${apiBase}${path}`;
};

const getAttachmentDownloadName = (file) =>
  file?.file_name || file?.name || file?.original_name || "attachment";

const sortMessagesAscending = (list = []) =>
  [...list].sort((a, b) => {
    const aTime = new Date(a?.created_at || a?.updated_at || 0).getTime();
    const bTime = new Date(b?.created_at || b?.updated_at || 0).getTime();

    if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) {
      return aTime - bTime;
    }

    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });

// Own messages (ticket owner) go to the right; admin/others go to the left
const isOwnMessage = (message, ticket) => {
  const ticketUserName = (ticket?.user?.name || "").trim();
  const messageUserName = (
    message?.user?.name ||
    message?.sender_name ||
    message?.admin?.name ||
    ""
  ).trim();
  const messageUserNameLower = messageUserName.toLowerCase();

  if (
    message?.is_admin === true ||
    message?.is_admin === 1 ||
    message?.sender_type === "admin" ||
    message?.sender === "admin" ||
    message?.from === "admin" ||
    message?.user_type === "admin" ||
    message?.role === "admin" ||
    message?.user?.is_admin ||
    message?.admin ||
    messageUserNameLower.includes("admin")
  ) {
    return false;
  }

  // Different display name → other side (admin / support)
  if (ticketUserName && messageUserName && ticketUserName !== messageUserName) {
    return false;
  }

  const ticketUserId = ticket?.user?.id ?? ticket?.user_id;
  const messageUserId =
    message?.user_id ?? message?.user?.id ?? message?.sender_id;

  if (ticketUserId != null && messageUserId != null) {
    return String(ticketUserId) === String(messageUserId);
  }

  if (ticketUserName && messageUserName) {
    return ticketUserName === messageUserName;
  }

  return false;
};

const getSenderName = (message, isOwn) => {
  if (!isOwn) {
    return (
      message?.user?.name ||
      message?.sender_name ||
      message?.admin?.name ||
      "پشتیبانی"
    );
  }
  return message?.user?.name || message?.sender_name || "شما";
};

const getStatusBadgeClass = (statusValue) => {
  switch (statusValue) {
    case 1:
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case 2:
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case 3:
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case 4:
      return "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200";
    case 5:
      return "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
  }
};

const getPriorityBadgeClass = (priorityValue) => {
  switch (priorityValue) {
    case 1:
      return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
    case 2:
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case 3:
      return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
    case 4:
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
  }
};

const showApiError = (error, fallback) => {
  const apiErrors = error.response?.data?.errors;
  if (apiErrors) {
    const firstError = Object.values(apiErrors)[0];
    handleToast(
      "error",
      Array.isArray(firstError) ? firstError[0] : firstError
    );
    return;
  }
  handleToast("error", error.response?.data?.message || fallback);
};

export const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(contextApi);
  const chatEndRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [downloadingUuid, setDownloadingUuid] = useState(null);

  const handleClick = (buttonName) => {
    context.recordClick(buttonName);
  };

  const handleDownloadAttachment = async (event, file) => {
    event.preventDefault();

    const uuid = file?.uuid;
    if (!uuid) {
      const fallbackUrl = getAttachmentUrl(file);
      if (fallbackUrl) window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return;
    }

    try {
      setDownloadingUuid(uuid);
      const response = await api.get(`media/${uuid}/download`, {
        responseType: "blob",
        headers: {
          Accept: "*/*",
          authorization: `Bearer ${localStorage.getItem("login")}`,
        },
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", getAttachmentDownloadName(file));
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      showApiError(error, "خطا در دانلود فایل");
    } finally {
      setDownloadingUuid(null);
    }
  };

  const fetchTicket = async () => {
    const response = await api.get(`tickets/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data?.data || response.data || null;
  };

  const fetchMessages = async () => {
    const response = await api.get(`tickets/${id}/messages?per_page=100`, {
      headers: getAuthHeaders(),
    });
    return sortMessagesAscending(extractList(response.data));
  };

  const loadTicketData = async () => {
    setLoading(true);
    try {
      const ticketData = await fetchTicket();
      setTicket(ticketData);

      try {
        const messageList = await fetchMessages();
        if (messageList.length) {
          setMessages(messageList);
        } else {
          setMessages(
            sortMessagesAscending(extractList(ticketData?.messages))
          );
        }
      } catch {
        setMessages(sortMessagesAscending(extractList(ticketData?.messages)));
      }
    } catch (error) {
      console.error("Error fetching ticket:", error);
      showApiError(error, "خطا در دریافت جزئیات تیکت");
      setTicket(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadTicketData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const statusValue = getFieldValue(ticket?.status);
  const priorityValue = getFieldValue(ticket?.priority);
  const isClosed =
    statusValue === CLOSED_STATUS || Boolean(ticket?.closed_at);

  const handleSendReply = async () => {
    const message = replyText.trim();
    if (!message || sending || isClosed) return;

    handleClick("ارسال پاسخ تیکت کلیک شد");
    setSending(true);
    try {
      await api.post(
        `tickets/${id}/messages`,
        { message },
        { headers: getAuthHeaders() }
      );
      setReplyText("");
      handleToast("success", "پیام با موفقیت ارسال شد");
      const [ticketData, messageList] = await Promise.all([
        fetchTicket(),
        fetchMessages().catch(() => []),
      ]);
      setTicket(ticketData);
      setMessages(
        messageList.length
          ? messageList
          : sortMessagesAscending(extractList(ticketData?.messages))
      );
    } catch (error) {
      console.error("Error sending reply:", error);
      showApiError(error, "خطا در ارسال پیام");
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    handleClick("بستن تیکت کلیک شد");
    setClosing(true);
    try {
      await api.patch(`tickets/${id}/close`, {}, { headers: getAuthHeaders() });
      handleToast("success", "تیکت با موفقیت بسته شد");
      setShowCloseConfirm(false);
      const ticketData = await fetchTicket();
      setTicket(ticketData);
    } catch (error) {
      console.error("Error closing ticket:", error);
      showApiError(error, "خطا در بستن تیکت");
    } finally {
      setClosing(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendReply();
    }
  };

  return (
    <div className="w-full min-h-screen bg-white dark:bg-[#0D1117]">
      <ToastContainer />
      <div className="mb-4 p-3 lg:px-6 lg:pt-6">
        <h1
          className="
            relative inline-block text-3xl font-bold mb-4
            text-[#0010c6] dark:text-white
            after:content-[''] after:block after:w-full after:h-[4px]
            after:bg-[#0010c6] after:mt-[10px] dark:after:bg-[#ffffff]
          "
        >
          جزئیات تیکت
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <MagnifyingGlass
            visible={true}
            height="80"
            width="80"
            ariaLabel="magnifying-glass-loading"
            wrapperStyle={{}}
            wrapperClass="magnifying-glass-wrapper"
            glassColor="#c0efff"
            color="#0010c6"
          />
        </div>
      ) : !ticket ? (
        <div className="w-[95%] mx-auto text-center text-gray-500 dark:text-gray-400">
          تیکت مورد نظر پیدا نشد.
          <button
            type="button"
            onClick={() => navigate("/support")}
            className="block mx-auto mt-4 text-sm bg-[#0010c6] hover:bg-blue-800 px-4 py-2 rounded-xl text-white"
          >
            بازگشت به لیست
          </button>
        </div>
      ) : (
        <div className="w-[95%] max-w-5xl mx-auto pb-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <button
              type="button"
              onClick={() => {
                handleClick("بازگشت از جزئیات تیکت کلیک شد");
                navigate("/support");
              }}
              className="text-sm bg-slate-600 hover:bg-slate-700 px-4 py-2 rounded-xl text-white"
            >
              بازگشت
            </button>
            {!isClosed && (
              <button
                type="button"
                onClick={() => {
                  handleClick("درخواست بستن تیکت کلیک شد");
                  setShowCloseConfirm(true);
                }}
                className="text-sm bg-red-600/90 hover:bg-red-700 px-4 py-2 rounded-xl text-white"
              >
                بستن تیکت
              </button>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#161B22] p-4 md:p-5 mb-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg md:text-xl font-semibold text-slate-800 dark:text-white truncate">
                  {ticket.subject || "-"}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  شناسه تیکت: {toPersianDigits(ticket.id)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                    statusValue
                  )}`}
                >
                  {getFieldLabel(ticket.status, STATUS_OPTIONS)}
                </span>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityBadgeClass(
                    priorityValue
                  )}`}
                >
                  {getFieldLabel(ticket.priority, PRIORITY_OPTIONS)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {[
                { label: "کاربر", value: ticket.user?.name || "-" },
                {
                  label: "دسته‌بندی",
                  value: ticket.category?.name || ticket.category?.title || "-",
                },
                {
                  label: "ایجاد",
                  value: formatMessageDate(ticket.created_at),
                },
                {
                  label: "بروزرسانی",
                  value: formatMessageDate(ticket.updated_at),
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl bg-white dark:bg-[#0D1117] border border-slate-100 dark:border-slate-700 px-3 py-2"
                >
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200 truncate">
                    {item.value}
                  </p>
                </div>
              ))}
              {ticket.closed_at && (
                <div className="rounded-xl bg-white dark:bg-[#0D1117] border border-slate-100 dark:border-slate-700 px-3 py-2">
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-0.5">
                    بسته شده
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200">
                    {formatMessageDate(ticket.closed_at)}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#161B22] overflow-hidden shadow-sm flex flex-col">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-100">
                گفتگو
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {toPersianDigits(messages.length)} پیام
              </span>
            </div>

            <div
              className="px-3 md:px-5 py-4 max-h-[35vh] min-h-[320px] overflow-y-auto bg-[#eef2f7] dark:bg-[#0b0f14]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(100,116,139,0.16) 1px, transparent 0)",
                backgroundSize: "16px 16px",
              }}
            >
              {messages.length === 0 ? (
                <div className="h-full min-h-[240px] flex items-center justify-center">
                  <p className="text-sm text-slate-400 dark:text-slate-500 bg-white/80 dark:bg-[#161B22]/80 px-4 py-2 rounded-full">
                    هنوز پیامی ثبت نشده است
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    const isOwn = isOwnMessage(message, ticket);
                    const attachments = getAttachments(message);
                    const senderName = getSenderName(message, isOwn);

                    return (
                      <div
                        key={message.id || index}
                        className="flex w-full"
                        style={{ direction: "ltr" }}
                      >
                        <div
                          className={`flex items-end gap-2 max-w-[78%] md:max-w-[55%] ${
                            isOwn
                              ? "ml-auto flex-row-reverse"
                              : "mr-auto"
                          }`}
                        >
                          <div
                            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                              isOwn
                                ? "bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-100"
                                : "bg-slate-300 text-slate-700 dark:bg-slate-600 dark:text-slate-100"
                            }`}
                          >
                            {getInitial(senderName)}
                          </div>

                          <div className="min-w-0" style={{ direction: "rtl" }}>
                            <p
                              className={`text-[11px] mb-1 px-1 text-slate-400 ${
                                isOwn ? "text-right" : "text-left"
                              }`}
                            >
                              {senderName}
                            </p>

                            <div
                              className={`relative px-3.5 py-2.5 text-sm leading-6 shadow-sm ${
                                isOwn
                                  ? "bg-[#dbeafe] dark:bg-[#243447] text-slate-800 dark:text-slate-100 rounded-2xl rounded-br-md border border-sky-200/70 dark:border-slate-600"
                                  : "bg-white dark:bg-[#21262d] text-slate-700 dark:text-slate-100 rounded-2xl rounded-bl-md border border-slate-200/80 dark:border-slate-600"
                              }`}
                            >
                              <p className="whitespace-pre-wrap break-words text-right">
                                {getMessageText(message)}
                              </p>

                              {attachments.length > 0 && (
                                <div className="mt-2.5 space-y-1.5">
                                  {attachments.map((file, fileIndex) => {
                                    const url = getAttachmentUrl(file);
                                    const name = getAttachmentName(file);
                                    const chipClass =
                                      "inline-flex items-center gap-1.5 max-w-full rounded-lg px-2.5 py-1.5 text-xs bg-white/80 dark:bg-slate-700/80 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600";

                                    return url ? (
                                      <a
                                        key={file.uuid || file.id || fileIndex}
                                        href={url}
                                        rel="noreferrer"
                                        style={{ margin: "0 4px" }}
                                        onClick={(event) =>
                                          handleDownloadAttachment(event, file)
                                        }
                                        className={`${chipClass} hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors ${
                                          downloadingUuid === file.uuid
                                            ? "opacity-60 pointer-events-none"
                                            : ""
                                        }`}
                                      >
                                        <PaperClipIcon className="w-3.5 h-3.5 shrink-0 opacity-70" />
                                        <span className="truncate">{name}</span>
                                      </a>
                                    ) : (
                                      <span
                                        key={file.uuid || file.id || fileIndex}
                                        className={chipClass}
                                      >
                                        <PaperClipIcon className="w-3.5 h-3.5 shrink-0 opacity-70" />
                                        <span className="truncate">{name}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}

                              <p className="mt-1.5 text-[10px] text-slate-400 dark:text-slate-500 text-left">
                                {formatMessageDate(
                                  message.created_at || message.updated_at
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {isClosed ? (
              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm text-center text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-[#0D1117]">
                این تیکت بسته شده و امکان ارسال پیام وجود ندارد.
              </div>
            ) : (
              <div className="px-3 md:px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#0D1117]">
                <div className="flex items-end gap-2 rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#161B22] p-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={2}
                    placeholder="پیام خود را بنویسید... (Enter برای ارسال)"
                    className="flex-1 resize-none border-0 bg-transparent px-2 py-2 text-sm text-slate-800 dark:text-slate-100 outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    disabled={!replyText.trim() || sending}
                    onClick={handleSendReply}
                    className="shrink-0 self-end mb-0.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 text-white text-sm px-4 py-2 transition-colors"
                  >
                    {sending ? "..." : "ارسال"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showCloseConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-[#161B22] p-5 shadow-xl border border-slate-200 dark:border-slate-700">
            <h3 className="text-base font-semibold text-slate-800 dark:text-white mb-2">
              بستن تیکت
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-300 mb-5 leading-6">
              بعد از بستن تیکت امکان ارسال پیام وجود ندارد. ادامه می‌دهید؟
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowCloseConfirm(false)}
                className="w-1/2 text-sm bg-slate-600 hover:bg-slate-700 px-3 py-2 rounded-xl text-white"
              >
                انصراف
              </button>
              <button
                type="button"
                disabled={closing}
                onClick={handleCloseTicket}
                className="w-1/2 text-sm bg-red-600 hover:bg-red-700 disabled:bg-slate-400 px-3 py-2 rounded-xl text-white"
              >
                {closing ? "در حال بستن..." : "بستن تیکت"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
